import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Phone, MapPin, Clock } from "lucide-react";
import { api } from "../lib/api";
import { NAP, toE164, toDisplay } from "../lib/nap";

// TikTok icon (Lucide doesn't ship one)
function TikTokIcon({ size = 16 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.85a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.28z"/>
    </svg>
  );
}

export default function Footer() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get("/contact-info").then((r) => setInfo(r.data)).catch(() => {});
  }, []);
  const year = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="relative bg-[#070707] border-t border-[#C9A961]/15 pt-24 pb-10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-16">
          <img src="/assets/logo-palazzolo.png" alt="Beauty Salon by Itria Leone" loading="lazy" className="h-20 w-auto mx-auto mb-6 opacity-95" />
          <div className="font-display text-3xl md:text-4xl tracking-[0.18em] uppercase">{NAP.displayName}</div>
          <div className="font-script text-2xl text-[#C9A961] mt-2">{NAP.byLine} · {NAP.city}</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 max-w-5xl mx-auto text-center">
          <div>
            <div className="text-[#C9A961] mb-3 flex justify-center"><MapPin size={18} /></div>
            <div className="font-display-sc text-[10px] tracking-[0.35em] text-white/50">Indirizzo</div>
            <div className="font-display text-base mt-2 leading-snug">
              {info?.address || NAP.address}
            </div>
          </div>
          <div>
            <div className="text-[#C9A961] mb-3 flex justify-center"><Phone size={18} /></div>
            <div className="font-display-sc text-[10px] tracking-[0.35em] text-white/50">Contatti</div>
            <a href={`tel:${toE164(info?.landline || NAP.landline)}`} data-testid="footer-landline" className="font-display text-base mt-2 block hover:text-[#C9A961]">
              {toDisplay(info?.landline, NAP.landline)}
            </a>
            <a href={`tel:${toE164(info?.phone || NAP.mobile)}`} data-testid="footer-phone" className="font-display text-sm mt-1 block text-white/70 hover:text-[#C9A961]">
              {toDisplay(info?.phone, NAP.mobile)}
            </a>
          </div>
          <div>
            <div className="text-[#C9A961] mb-3 flex justify-center"><Clock size={18} /></div>
            <div className="font-display-sc text-[10px] tracking-[0.35em] text-white/50">Orari</div>
            <div className="font-display text-sm mt-2 leading-relaxed">
              {NAP.hours.map((h) => (
                <div key={h.days}>{h.days}: {h.time}</div>
              ))}
            </div>
          </div>
        </div>

        {/* Social: mostrati solo se impostati dal pannello admin > Contatti */}
        <div className="flex justify-center gap-3 mt-14">
          {info?.instagram && (
          <a href={info.instagram} data-testid="footer-instagram" target="_blank" rel="noreferrer" className="w-11 h-11 border border-[#C9A961]/40 hover:bg-[#C9A961] hover:text-[#0a0a0a] flex items-center justify-center transition">
            <Instagram size={16} />
          </a>
          )}
          {info?.facebook && (
          <a href={info.facebook} data-testid="footer-facebook" target="_blank" rel="noreferrer" className="w-11 h-11 border border-[#C9A961]/40 hover:bg-[#C9A961] hover:text-[#0a0a0a] flex items-center justify-center transition">
            <Facebook size={16} />
          </a>
          )}
          {info?.tiktok && (
          <a href={info.tiktok} data-testid="footer-tiktok" target="_blank" rel="noreferrer" className="w-11 h-11 border border-[#C9A961]/40 hover:bg-[#C9A961] hover:text-[#0a0a0a] flex items-center justify-center transition">
            <TikTokIcon size={16} />
          </a>
          )}
          <a href={`https://wa.me/${toE164(info?.whatsapp || NAP.whatsapp).replace(/\D/g, "")}`} data-testid="footer-whatsapp" target="_blank" rel="noreferrer" className="px-4 h-11 border border-[#C9A961]/40 hover:bg-[#C9A961] hover:text-[#0a0a0a] flex items-center justify-center transition text-[10px] uppercase tracking-[0.3em]">
            WhatsApp
          </a>
        </div>

        <div className="h-line my-14" />

        <div className="flex flex-wrap items-center justify-center gap-6 text-[10px] uppercase tracking-[0.3em] text-white/40 text-center">
          {[["/", "Home"], ["/chi-siamo", "Chi Siamo"], ["/chi-siamo/storia", "Storia"], ["/servizi", "Servizi"], ["/servizi/listino", "Listino"], ["/staff", "Staff"], ["/galleria", "Galleria"], ["/contatti", "Contatti"], ["/prenotazioni", "Prenota"]].map(([to, label]) => (
            <Link key={to} to={to} data-testid={`footer-link-${label.toLowerCase().replace(/\s/g, "-")}`} className="hover:text-[#C9A961] transition">
              {label}
            </Link>
          ))}
          <Link to="/privacy-policy" data-testid="footer-link-privacy-policy" className="hover:text-[#C9A961] transition">
            Privacy Policy
          </Link>
          <Link to="/cookie-policy" data-testid="footer-link-cookie-policy" className="hover:text-[#C9A961] transition">
            Cookie Policy
          </Link>
          <button
            type="button"
            data-testid="footer-cookie-preferences"
            onClick={() => window.dispatchEvent(new Event("open-cookie-preferences"))}
            className="uppercase tracking-[0.3em] hover:text-[#C9A961] transition"
          >
            Preferenze cookie
          </button>
        </div>

        <div className="mt-10 flex flex-col items-center gap-3 text-[10px] tracking-wider text-white/35 text-center">
          <div>
            © {year} <span className="text-white/50">{NAP.name}</span>{info?.vat && <> · P.IVA <span className="text-white/55" data-testid="footer-vat">{info.vat}</span></>} · Tutti i diritti riservati
          </div>
          <div>
            Sito sviluppato da{" "}
            <a
              href="https://ideamedia.org"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-ideamedia"
              className="text-[#C9A961] hover:text-[#E0C087] tracking-[0.2em]"
            >
              IdeaMedia
            </a>
            {" · "}
            <Link to="/admin/login" data-testid="admin-login-link" className="hover:text-[#C9A961]">
              Area riservata
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
